import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import Logger from 'core/utils/Logger'
import { TPartnerComplianceStatus } from 'onboarding/classes/models/partnerCompliance'
import { showError } from 'onboarding/classes/services/notification'
import { SimplePageLayout } from 'onboarding/components/layout/simplePageLayout/SimplePageLayout'
import { RejectPartner } from 'onboarding/components/page/partnerCompliancePage/RejectPartner'
import { SimpleResultContent } from 'onboarding/components/page/simpleResultContent/SimpleResultContent'
import { onboardingAuthService } from 'onboarding/services/authService'
import { partnerComplianceService } from 'onboarding/services/partnerComplianceService'

const MESSAGE_NO_REQUIRED_DATA = 'Required data is not provided'
const ALLOWED_STATUSES: TPartnerComplianceStatus[] = ['approved', 'rejected']
const checkIsStatusValid = (status?: string): status is TPartnerComplianceStatus =>
  Boolean(status && ALLOWED_STATUSES.includes(status as TPartnerComplianceStatus))

export const PartnerCompliancePage = () => {
  const [isSaving, setIsSaving] = useState(false)
  const [isSaved, setIsSaved] = useState(false)
  const [comment, setComment] = useState('')
  const [success, setSuccess] = useState(false)
  const [message, setMessage] = useState('')
  const [isAuthorizing, setIsAuthorizing] = useState(false)
  const [isAuthorized, setIsAuthorized] = useState(false)
  const { status: rawStatus, token: rawToken } = useParams<{
    status: TPartnerComplianceStatus
    token: string
  }>()
  const status = (rawStatus ?? '').trim()
  const token = (rawToken ?? '').trim()

  const setSuccessMessage = useCallback((success: boolean, message: string) => {
    setSuccess(success)
    setMessage(message)
  }, [])

  const saveCompliance = useCallback(
    async (comment?: string) => {
      if (!checkIsStatusValid(status)) {
        setSuccessMessage(false, MESSAGE_NO_REQUIRED_DATA)

        return
      }
      if (status === 'rejected' && !comment) {
        showError('Rejection reason is required')

        return
      }
      setComment(comment ?? '')

      try {
        setIsSaving(true)
        await partnerComplianceService.save({ status, comment }, token)
        setIsSaved(true)
        setSuccessMessage(true, `Partner is ${status} successfully`)
      } catch (e: any) {
        Logger.error(e)
        setSuccessMessage(false, e.message || 'Failed to save the compliance')
      } finally {
        setIsSaving(false)
      }
    },
    [status, token, setSuccessMessage]
  )

  const auth = useCallback(
    async (token: string) => {
      let isAuthorized = false
      try {
        setIsAuthorizing(true)
        if (token) {
          isAuthorized = await onboardingAuthService.authByToken(token)
        }
        setSuccessMessage(isAuthorized, isAuthorized ? 'Authorized successfully' : 'Not Authorized')
      } catch (e: any) {
        Logger.error(e)
        setSuccessMessage(false, e.message || 'Failed to authorize')
      } finally {
        setIsAuthorizing(false)
      }
      setIsAuthorized(isAuthorized)

      return isAuthorized
    },
    [setSuccessMessage]
  )

  useEffect(() => {
    const process = async () => {
      if (token && checkIsStatusValid(status)) {
        const isAuthorized = await auth(token)
        if (isAuthorized && status === 'approved') {
          await saveCompliance()
        }
      } else {
        setSuccessMessage(false, MESSAGE_NO_REQUIRED_DATA)
      }
    }

    process()
  }, [status, token])

  return (
    <SimplePageLayout>
      <SimpleResultContent
        success={success}
        message={message}
        isLoading={isAuthorizing || isSaving}
        loadingText={isAuthorizing ? 'Authentication...' : undefined}
        showMessage
      >
        {isAuthorized && status === 'rejected' && !isSaved ? (
          <RejectPartner onSave={saveCompliance} comment={comment} />
        ) : null}
      </SimpleResultContent>
    </SimplePageLayout>
  )
}
